/**
 * Children Management Page (Refactored)
 * Manages child profiles with CRUD operations
 *
 * Refactored from 466 LOC monolith to component-based architecture
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChildren } from '../../hooks/useChildren';
import { useSubscription } from '../../hooks/useSubscription';
import { ChildThemePicker } from '../../components/ChildThemePicker';
import { AIActivityWizard } from '../../components/AIActivityWizard';

// Extracted components
import { ChildCard } from './components/ChildCard';
import { ChildFormModal } from './components/ChildFormModal';
import { SubscriptionBanner, HeaderActions } from './components/SubscriptionBanner';
import { EmptyChildrenState } from './components/EmptyChildrenState';

const avatarColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#F97316'];

interface ChildForm {
  name: string;
  age: number;
  avatar_color: string;
}

export function ChildrenManagementPage() {
  const navigate = useNavigate();
  const { children, addChild, updateChild } = useChildren();
  const { getMaxChildren, getCurrentPlan, hasValidSubscription, isLifetime } = useSubscription();

  // State management
  const [showAddChild, setShowAddChild] = useState(false);
  const [editingChild, setEditingChild] = useState<string | null>(null);
  const [childForm, setChildForm] = useState<ChildForm>({
    name: '',
    age: 5,
    avatar_color: avatarColors[0]
  });
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [selectedChildForTheme, setSelectedChildForTheme] = useState<string | null>(null);
  const [showWizard, setShowWizard] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Subscription info
  const maxChildren = getMaxChildren();
  const currentPlan = getCurrentPlan();
  const canAddChild = hasValidSubscription() && maxChildren > 0 && children.length < maxChildren;

  // Event handlers
  const handleAddChild = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (childForm.name.trim()) {
      const result = await addChild(childForm);

      if (result.error) {
        setError(result.error.message);
        return;
      }

      resetForm();
      setShowAddChild(false);
    }
  };

  const handleEditChild = (child: { id: string; name: string; age: number; avatar_color: string }) => {
    setEditingChild(child.id);
    setChildForm({
      name: child.name,
      age: child.age,
      avatar_color: child.avatar_color,
    });
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingChild) {
      await updateChild(editingChild, childForm);
      setEditingChild(null);
      resetForm();
    }
  };

  const handleCustomizeTheme = (childId: string) => {
    setSelectedChildForTheme(childId);
    setShowThemePicker(true);
  };

  const handleWizardComplete = () => {
    setShowWizard(false);
  };

  const handleFormChange = (field: keyof ChildForm, value: string | number) => {
    setChildForm(prev => ({ ...prev, [field]: value }));
  };

  const handleUpgradeClick = () => {
    setShowAddChild(false);
    setError(null);
    navigate('/pricing');
  };

  const resetForm = () => {
    setChildForm({ name: '', age: 5, avatar_color: avatarColors[0] });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 font-baloo">Children Management</h1>
          <p className="text-gray-600 mt-2">Add and manage your children's learning profiles</p>
        </div>
        <HeaderActions
          hasValidSubscription={hasValidSubscription()}
          canAddChild={canAddChild}
          onSubscribeClick={() => navigate('/pricing')}
          onWizardClick={() => setShowWizard(true)}
          onAddChildClick={() => setShowAddChild(true)}
        />
      </div>

      {/* Subscription Banner */}
      <SubscriptionBanner
        hasValidSubscription={hasValidSubscription()}
        isLifetime={isLifetime()}
        currentPlan={currentPlan}
        childrenCount={children.length}
        maxChildren={maxChildren}
        onSubscribeClick={() => navigate('/pricing')}
        onUpgradeClick={() => navigate('/pricing')}
      />

      {/* Children Grid or Empty State */}
      {children.length === 0 ? (
        <EmptyChildrenState
          onWizardClick={() => setShowWizard(true)}
          onAddChildClick={() => setShowAddChild(true)}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {children.map((child) => (
            <ChildCard
              key={child.id}
              child={child}
              onEdit={handleEditChild}
              onCustomizeTheme={handleCustomizeTheme}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      <ChildFormModal
        isOpen={showAddChild}
        mode="add"
        formData={childForm}
        error={error}
        onSubmit={handleAddChild}
        onClose={() => {
          setShowAddChild(false);
          setError(null);
        }}
        onChange={handleFormChange}
        onUpgradeClick={handleUpgradeClick}
      />

      <ChildFormModal
        isOpen={!!editingChild}
        mode="edit"
        formData={childForm}
        error={null}
        onSubmit={handleSaveEdit}
        onClose={() => setEditingChild(null)}
        onChange={handleFormChange}
      />

      {showThemePicker && selectedChildForTheme && (
        <ChildThemePicker
          isOpen={showThemePicker}
          onClose={() => {
            setShowThemePicker(false);
            setSelectedChildForTheme(null);
          }}
          childId={selectedChildForTheme}
          childName={children.find(c => c.id === selectedChildForTheme)?.name || ''}
        />
      )}

      {showWizard && (
        <AIActivityWizard
          mode="new-child"
          onClose={handleWizardComplete}
        />
      )}
    </div>
  );
}
